/**
 * useSupplierFeeData
 * SupplierFeeManagementPage의 데이터 페칭, 상태 관리, 핸들러를 담당하는 훅
 */
import { useEffect, useState, useCallback } from 'react';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { usePagination } from '@/shared/hooks/usePagination';
import { getItem } from '@/shared/utils/storage';
import { useUserInfo } from '@/shared/hooks/useCrudOperations';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import { riderFeeApi, RiderFeeDto } from '../api/riderFeeApi';
import { useSupplierFeeHandlers } from './useSupplierFeeHandlers';
import { RiderCommissionRow, FILTER_STORAGE_KEY, ITEMS_PER_PAGE } from '../utils/supplierFeeHelpers';

interface UseSupplierFeeDataProps {
    selectedSupplierId?: string;
    onUnsavedChanges?: (hasChanges: boolean) => void;
}

export const useSupplierFeeData = ({ selectedSupplierId, onUnsavedChanges }: UseSupplierFeeDataProps) => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    // ======== State - 데이터 ========
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [commissionData, setCommissionData] = useState<RiderCommissionRow[]>([]);
    const [filteredData, setFilteredData] = useState<RiderCommissionRow[]>([]);

    // ======== State - 필터 ========
    const [filters, setFilters] = useState(() => {
        const saved = loadFilters<{ riderName: string; riderId: string; statusFilter: string }>(FILTER_STORAGE_KEY);
        return saved || { riderName: '', riderId: '', statusFilter: '' };
    });
    const [statusFilter, setStatusFilter] = useState<string>(filters.statusFilter || '');

    // ======== State - 일괄 저장 적용 예정일 모달 ========
    const [showBatchFeeApplyModal, setShowBatchFeeApplyModal] = useState(false);
    const [batchFeeApplyDateInfo, setBatchFeeApplyDateInfo] = useState<{
        applyStartDate: string;
        isImmediateApply: boolean;
    } | null>(null);

    // ======== 권한 체크 ========
    const isReadOnly = getItem<string>('USER_TYPE') !== 'SUPPLIER';

    // ======== 페이지네이션 ========
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: filteredData, itemsPerPage: ITEMS_PER_PAGE.DEFAULT });

    // ======== 데이터 로드 ========
    const fetchRiderFees = useCallback(async () => {
        try {
            setLoading(true);
            setError(false);

            const userInfo = getUserInfo();
            if (!userInfo) {
                setLoading(false);
                return;
            }

            const targetSupplierId = selectedSupplierId || userInfo.suppId;
            if (!targetSupplierId) {
                showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
                setLoading(false);
                return;
            }

            const riderFees: RiderFeeDto[] = await riderFeeApi.getRiderFees(targetSupplierId);

            const rowData: RiderCommissionRow[] = riderFees.map(fee => ({
                riderId: fee.riderId,
                riderName: fee.riderName,
                commissionType: fee.commissionType || '',
                amount: fee.amount ? fee.amount.toString() : '',
                applyDate: fee.applyDate || '',
                isEdited: false,
                contStatus: fee.contStatus || ''
            }));

            // 계약 중인 라이더가 먼저, 계약 종료 라이더가 뒤에 오도록 정렬
            rowData.sort((a, b) => {
                const aUnderContract = a.contStatus === 'UNDER_CONTRACT';
                const bUnderContract = b.contStatus === 'UNDER_CONTRACT';
                if (aUnderContract && !bUnderContract) return -1;
                if (!aUnderContract && bUnderContract) return 1;
                return 0;
            });

            setCommissionData(rowData);
            setLoading(false);
        } catch {
            setError(true);
            showToast('라이더 수수료 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setLoading(false);
        }
    }, [selectedSupplierId]);

    const handleRetry = useCallback(() => {
        fetchRiderFees();
    }, [fetchRiderFees]);

    // ======== 핸들러 훅 ========
    const supplierFeeHandlers = useSupplierFeeHandlers({
        selectedSupplierId,
        commissionData,
        setCommissionData,
        fetchRiderFees
    });

    // ======== Side Effects ========
    useEffect(() => {
        fetchRiderFees();
    }, [fetchRiderFees]);

    // commissionData 변경 시 filteredData 업데이트
    useEffect(() => {
        const savedFilters = loadFilters<{ riderName: string; riderId: string; statusFilter: string }>(FILTER_STORAGE_KEY);
        if (savedFilters && commissionData.length > 0) {
            let filtered = [...commissionData];

            if (savedFilters.riderName) {
                filtered = filtered.filter(row =>
                    row.riderName.toLowerCase().includes(savedFilters.riderName.toLowerCase())
                );
            }
            if (savedFilters.riderId) {
                filtered = filtered.filter(row =>
                    row.riderId.toLowerCase().includes(savedFilters.riderId.toLowerCase())
                );
            }
            if (savedFilters.statusFilter) {
                filtered = filtered.filter(row => row.contStatus === savedFilters.statusFilter);
            }

            setFilteredData(filtered);
        } else {
            setFilteredData(commissionData);
        }
    }, [commissionData]);

    // 수정 여부를 부모 컴포넌트에 알림
    useEffect(() => {
        const hasEdited = commissionData.some(row => row.isEdited);
        onUnsavedChanges?.(hasEdited);
    }, [commissionData, onUnsavedChanges]);

    // ======== 일괄 저장 ========
    const handleBatchSave = async () => {
        const editedRows = commissionData.filter(row => row.isEdited);

        if (editedRows.length === 0) {
            showToast('수정된 항목이 없습니다.', { type: ToastType.WARNING });
            return;
        }

        const userInfo = getUserInfo();
        if (!userInfo) {
            showToast('사용자 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        const targetSupplierId = selectedSupplierId || userInfo.suppId;
        if (!targetSupplierId) {
            showToast('협력사 정보를 찾을 수 없습니다.', { type: ToastType.ERROR });
            return;
        }

        const invalidRows = editedRows.filter(row => {
            if (!row.commissionType) return true;
            const isFixed = row.commissionType === 'PER_CASE_FIXED' || row.commissionType === 'PER_DAY_FIXED';
            const amount = parseFloat(row.amount);
            if (isFixed && (isNaN(amount) || amount < 0)) return true;
            if (!isFixed && (isNaN(amount) || amount < 0 || amount > 100)) return true;
            return false;
        });

        if (invalidRows.length > 0) {
            showToast(`유효하지 않은 데이터가 있습니다. (${invalidRows.length}건)`, { type: ToastType.WARNING });
            return;
        }

        if (!window.confirm(`${editedRows.length}건의 수수료를 일괄 저장하시겠습니까?`)) {
            return;
        }

        try {
            setLoading(true);

            const requests = editedRows.map(row => ({
                suppId: targetSupplierId,
                riderId: row.riderId,
                commissionType: row.commissionType,
                amount: parseFloat(row.amount),
                applyTiming: 'IMMEDIATE',
                userId: userInfo.userId
            }));

            const result = await riderFeeApi.saveRiderFeesBatch(requests);
            showToast(`${result.successCount}건이 저장되었습니다.`, { type: ToastType.SUCCESS });

            await fetchRiderFees();

            setBatchFeeApplyDateInfo({
                applyStartDate: result.applyStartDate,
                isImmediateApply: result.isImmediateApply
            });
            setShowBatchFeeApplyModal(true);
        } catch {
            showToast('일괄 저장에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    };

    // ======== 검색/필터 ========
    const handleSearch = () => {
        let filtered = [...commissionData];

        if (filters.riderName) {
            filtered = filtered.filter(row =>
                row.riderName.toLowerCase().includes(filters.riderName.toLowerCase())
            );
        }
        if (filters.riderId) {
            filtered = filtered.filter(row =>
                row.riderId.toLowerCase().includes(filters.riderId.toLowerCase())
            );
        }
        if (statusFilter) {
            filtered = filtered.filter(row => row.contStatus === statusFilter);
        }

        setFilteredData(filtered);
        resetPagination();
        saveFilters(FILTER_STORAGE_KEY, { ...filters, statusFilter });
    };

    const handleQuickFilter = useCallback((status: string) => {
        setStatusFilter(status);
        let filtered = [...commissionData];

        if (filters.riderName) {
            filtered = filtered.filter(row =>
                row.riderName.toLowerCase().includes(filters.riderName.toLowerCase())
            );
        }
        if (filters.riderId) {
            filtered = filtered.filter(row =>
                row.riderId.toLowerCase().includes(filters.riderId.toLowerCase())
            );
        }
        if (status) {
            filtered = filtered.filter(row => row.contStatus === status);
        }

        setFilteredData(filtered);
        resetPagination();
        saveFilters(FILTER_STORAGE_KEY, { ...filters, statusFilter: status });
    }, [commissionData, filters, resetPagination]);

    return {
        // state
        loading,
        error,
        commissionData,
        filters,
        setFilters,
        statusFilter,
        isReadOnly,
        // pagination
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        // batch modal state
        showBatchFeeApplyModal,
        setShowBatchFeeApplyModal,
        batchFeeApplyDateInfo,
        setBatchFeeApplyDateInfo,
        // handlers
        handleRetry,
        handleBatchSave,
        handleSearch,
        handleQuickFilter,
        // from useSupplierFeeHandlers
        ...supplierFeeHandlers,
    };
};
